// BEGIN FILE: src/utils/marriageCompatibility.ts
/**
 * Marriage Compatibility Analysis - Vedic Astrology
 * 
 * Comprehensive relationship compatibility analysis including:
 * - Moon sign distance (emotional dynamics)
 * - Gana compatibility (temperament)
 * - Nakshatra lord relationships
 * - Yoni compatibility (instinctual/sexual)
 * - Lagna (ascendant) compatibility
 * - 7th house analysis (marriage mechanics)
 * - Venus & Jupiter strength (capacity to love/sustain)
 */

import type { PlanetName } from './astroEngine';
import { classifyFunctionalForLagna } from './astroEngine';

// ===== TYPES =====

export interface PartnerChart {
  name: string;
  gender: 'male' | 'female';
  moonSign: number;           // 1-12 (Aries=1, Taurus=2, etc.)
  moonNakshatra: string;      // e.g., "Ashwini", "Bharani"
  moonNakshatraLord: PlanetName;
  lagna: number;              // 1-12
  lagnaSign: number;          // Ascendant sign (1-12) - same as lagna, for clarity
  h1Score: number;            // House 1 score
  h7Score: number;            // House 7 score
  planetsInH7: PlanetName[];  // Planets in 7th house
  aspectingH7: PlanetName[];  // Planets aspecting 7th house (especially Jupiter for protection)
  seventhLord: PlanetName;    // Lord of 7th house
  seventhLordSign: number;    // Sign where 7th lord is placed
  seventhLordHouse: number;   // House where 7th lord is placed
  seventhLordState: {
    exalted: boolean;
    debilitated: boolean;
    ownSign: boolean;
    combust: boolean;
    retrograde: boolean;
  };
  seventhLordAspects?: PlanetName[];  // Planets aspecting the 7th lord (optional)
  // Planet signs for D1 overlays
  marsSign: number;           // Mars sign (1-12)
  venusSign: number;          // Venus sign (1-12)
  saturnSign: number;         // Saturn sign (1-12)
  rahuSign: number;           // Rahu sign (1-12)
  mars: {
    house: number;            // House where Mars is placed (1-12)
    sign: number;             // Sign where Mars is placed (1-12)
    state: {
      exalted: boolean;       // In Capricorn
      debilitated: boolean;   // In Cancer
      ownSign: boolean;       // In Aries or Scorpio
      combust: boolean;
      retrograde: boolean;
    };
    aspectingPlanets: PlanetName[]; // Planets aspecting Mars
  };
  venus: {
    sign: number;
    house: number;
    state: {
      exalted: boolean;
      debilitated: boolean;
      ownSign: boolean;
      combust: boolean;
      retrograde: boolean;
    };
  };
  jupiter: {
    sign: number;
    house: number;
    state: {
      exalted: boolean;
      debilitated: boolean;
      ownSign: boolean;
      combust: boolean;
      retrograde: boolean;
    };
  };
  d9?: {
    lagnaSign: number;          // D9 Lagna sign (1-12)
    planetsInD9H1: PlanetName[]; // Planets in D9 1st house (Lagna)
    planetsInD9H7: PlanetName[]; // Planets in D9 7th house
    d9LagnaLord: PlanetName;     // Lord of D9 Lagna
    d9SeventhLord: PlanetName;   // Lord of D9 7th house
    lagnaLordSign: number;       // D9 Lagna lord in which D9 sign
    lagnaLordState: {
      exalted: boolean;
      debilitated: boolean;
      ownSign: boolean;
      friendly: boolean;
      combust: boolean;
      retrograde: boolean;
    };
    seventhLordSign: number;     // D9 7th lord in which D9 sign
    seventhLordState: {
      exalted: boolean;
      debilitated: boolean;
      ownSign: boolean;
      afflicted: boolean;
    };
    venusSign: number;           // Venus in D9 sign
    venusHouse: number;          // Venus in D9 house
    venusState: {
      exalted: boolean;
      debilitated: boolean;
      ownSign: boolean;
    };
    marsSign: number;            // Mars in D9 sign
    marsHouse: number;           // Mars in D9 house
    saturnSign: number;          // Saturn in D9 sign
    saturnHouse: number;         // Saturn in D9 house
  };
}

export interface MoonDistanceAnalysis {
  distance: string;           // e.g., "1-1", "3-11"
  relationshipName: string;   // e.g., "Same sign", "Trikona growth axis"
  meaning: string;
  outcome: string;
  score: 'high' | 'moderate' | 'low' | 'conditional';
  compatibilityPercent: number;
}

export interface GanaCompatibility {
  person1Gana: 'Deva' | 'Manushya' | 'Rakshasa';
  person2Gana: 'Deva' | 'Manushya' | 'Rakshasa';
  combo: string;              // e.g., "Deva–Manushya"
  outcome: string;
  adjustment: 'easy' | 'manageable' | 'requires-work' | 'major-adjustment';
  compatibilityPercent: number;
}

export interface NakshatraLordCompatibility {
  person1Lord: PlanetName;
  person2Lord: PlanetName;
  relationship: 'same' | 'friendly' | 'neutral' | 'enemy';
  outcome: string;
  compatibilityPercent: number;
}

export interface YoniCompatibility {
  person1Yoni: string;
  person2Yoni: string;
  relationship: 'same' | 'friendly' | 'neutral' | 'enemy';
  outcome: string;
  compatibilityPercent: number;
}

export interface LagnaCompatibility {
  distance: string;
  outcome: string;
  flags: string[];
  compatibilityPercent: number;
}

export interface SeventhHouseAnalysis {
  person: string;
  occupants: Array<{ planet: PlanetName; outcome: string }>;
  jupiterProtection: boolean;  // Jupiter aspect on 7th house
  seventhLordCondition: {
    lord: PlanetName;
    state: string;
    impact: string;
  };
}

export interface VenusJupiterAnalysis {
  person: string;
  venus: {
    state: string;
    outcome: string;
    strength: 'strong' | 'moderate' | 'weak';
  };
  jupiter: {
    state: string;
    outcome: string;
    strength: 'strong' | 'moderate' | 'weak';
  };
  overallCapacity: string;
}

export interface AshtakutaScore {
  varna: { score: number; max: 1; description: string };
  vashya: { score: number; max: 2; description: string };
  tara: { score: number; max: 3; description: string };
  yoni: { score: number; max: 4; description: string };
  grahaMaitri: { score: number; max: 5; description: string };
  gana: { score: number; max: 6; description: string };
  rashi: { score: number; max: 7; description: string };
  nadi: { score: number; max: 8; description: string };
  total: number;                  // Out of 36
  rating: 'excellent' | 'good' | 'average' | 'poor';
}

export interface MarriageCompatibilityResult {
  moonDistance: MoonDistanceAnalysis;
  gana: GanaCompatibility;
  nakshatraLord: NakshatraLordCompatibility;
  yoni: YoniCompatibility;
  lagna: LagnaCompatibility;
  seventhHouseP1: SeventhHouseAnalysis;
  seventhHouseP2: SeventhHouseAnalysis;
  venusJupiterP1: VenusJupiterAnalysis;
  venusJupiterP2: VenusJupiterAnalysis;
  ashtakuta: AshtakutaScore;      // Traditional 36-point system
  manglikP1: ManglikDoshaAnalysis; // Mangal/Kuja Dosha for Person 1
  manglikP2: ManglikDoshaAnalysis; // Mangal/Kuja Dosha for Person 2
  d9Analysis?: D9Analysis;         // Navamsa (D9) marriage reality analysis
  overallScore: number;           // 0-100
  overallRating: 'excellent' | 'good' | 'fair' | 'challenging';
  strengths: string[];
  challenges: string[];
  recommendations: string[];
}

export interface D9Analysis {
  person1Name: string;
  person2Name: string;
  p1D9Condition: {
    // D9 H1 (Lagna) Analysis
    planetsInD9H1: { planet: PlanetName; meaning: string }[];
    lagnaLordStrength: 'strong' | 'moderate' | 'weak';
    lagnaLordOutcome: string;
    h1Influence: 'benefic-dominant' | 'malefic-dominant' | 'mixed';
    // D9 H7 Analysis
    planetsInD9H7: { planet: PlanetName; tone: string }[];
    beneficsInD9H7: PlanetName[];
    maleficsInD9H7: PlanetName[];
    venusStrength: 'strong' | 'weak' | 'moderate';
    seventhLordCondition: 'strong' | 'weak' | 'afflicted';
    seventhLordMeaning: string;
    // H1-H7 Balance
    h1Strength: 'strong' | 'weak';
    h7Strength: 'strong' | 'weak';
    balancePattern: 'strong-strong' | 'strong-weak' | 'weak-strong' | 'weak-weak';
    balanceOutcome: string;
    outcome: string;
  };
  p2D9Condition: {
    // D9 H1 (Lagna) Analysis
    planetsInD9H1: { planet: PlanetName; meaning: string }[];
    lagnaLordStrength: 'strong' | 'moderate' | 'weak';
    lagnaLordOutcome: string;
    h1Influence: 'benefic-dominant' | 'malefic-dominant' | 'mixed';
    // D9 H7 Analysis
    planetsInD9H7: { planet: PlanetName; tone: string }[];
    beneficsInD9H7: PlanetName[];
    maleficsInD9H7: PlanetName[];
    venusStrength: 'strong' | 'weak' | 'moderate';
    seventhLordCondition: 'strong' | 'weak' | 'afflicted';
    seventhLordMeaning: string;
    // H1-H7 Balance
    h1Strength: 'strong' | 'weak';
    h7Strength: 'strong' | 'weak';
    balancePattern: 'strong-strong' | 'strong-weak' | 'weak-strong' | 'weak-weak';
    balanceOutcome: string;
    outcome: string;
  };
  crossChartChecks: {
    // Basic interactions
    p1VenusInP2D9H7: boolean;
    p2VenusInP1D9H7: boolean;
    p1VenusAspectsP2D9H7: boolean;
    p2VenusAspectsP1D9H7: boolean;
    p1LagnaLordInP2D9Lagna: boolean;
    p2LagnaLordInP1D9Lagna: boolean;
    p1MarsAfflictsP2D9H7: boolean;
    p2MarsAfflictsP1D9H7: boolean;
    p1MarsAspectsP2D9H7: boolean;
    p2MarsAspectsP1D9H7: boolean;
    bothVenusInSameSign: boolean;
    bothMarsInSameSign: boolean;
    // Advanced overlays (D9 Lagna focus)
    p1LagnaLordInP2D9H1: { present: boolean; meaning: string };
    p2LagnaLordInP1D9H1: { present: boolean; meaning: string };
    p1VenusInP2D9H1: { present: boolean; meaning: string };
    p2VenusInP1D9H1: { present: boolean; meaning: string };
    p1SaturnInP2D9H1: { present: boolean; meaning: string };
    p2SaturnInP1D9H1: { present: boolean; meaning: string };
    p1MarsInP2D9H1: { present: boolean; meaning: string };
    p2MarsInP1D9H1: { present: boolean; meaning: string };
  };
  d1Overlays: {
    // D1 (Birth Chart) Overlays - how partner's planets fall in your chart
    p1VenusInP2D1H7: { present: boolean; meaning: string };
    p2VenusInP1D1H7: { present: boolean; meaning: string };
    p1MoonInP2D1H4: { present: boolean; meaning: string };
    p2MoonInP1D1H4: { present: boolean; meaning: string };
    p1MarsInP2D1H7: { present: boolean; meaning: string };
    p2MarsInP1D1H7: { present: boolean; meaning: string };
    p1SaturnInP2D1H7: { present: boolean; meaning: string };
    p2SaturnInP1D1H7: { present: boolean; meaning: string };
    p1RahuInP2D1H7: { present: boolean; meaning: string };
    p2RahuInP1D1H7: { present: boolean; meaning: string };
  };
  overallD9Rating: 'excellent' | 'good' | 'fair' | 'challenging';
  interpretation: string;
}

export interface ManglikDoshaAnalysis {
  person: string;
  isManglik: boolean;
  marsHouse: number;            // House where Mars is placed (1-12)
  effect: string;               // Effect description based on house
  severity: 'high' | 'moderate' | 'low' | 'none';
  cancellationFactors: string[]; // List of cancellation factors present
  isCancelled: boolean;         // True if any cancellation factor exists
  recommendation: string;
  fromExistingData?: boolean;   // True if using pre-computed data
}

// ===== NAKSHATRA DATA =====

const NAKSHATRA_DATA: Record<string, {
  gana: 'Deva' | 'Manushya' | 'Rakshasa';
  lord: PlanetName;
  yoni: string;
}> = {
  'Ashwini': { gana: 'Deva', lord: 'Ketu', yoni: 'Horse' },
  'Bharani': { gana: 'Manushya', lord: 'Venus', yoni: 'Elephant' },
  'Krittika': { gana: 'Rakshasa', lord: 'Sun', yoni: 'Goat' },
  'Rohini': { gana: 'Manushya', lord: 'Moon', yoni: 'Serpent' },
  'Mrigashira': { gana: 'Deva', lord: 'Mars', yoni: 'Serpent' },
  'Ardra': { gana: 'Manushya', lord: 'Rahu', yoni: 'Dog' },
  'Punarvasu': { gana: 'Deva', lord: 'Jupiter', yoni: 'Cat' },
  'Pushya': { gana: 'Deva', lord: 'Saturn', yoni: 'Goat' },
  'Ashlesha': { gana: 'Rakshasa', lord: 'Mercury', yoni: 'Cat' },
  'Magha': { gana: 'Rakshasa', lord: 'Ketu', yoni: 'Rat' },
  'Purva Phalguni': { gana: 'Manushya', lord: 'Venus', yoni: 'Rat' },
  'Uttara Phalguni': { gana: 'Manushya', lord: 'Sun', yoni: 'Cow' },
  'Hasta': { gana: 'Deva', lord: 'Moon', yoni: 'Buffalo' },
  'Chitra': { gana: 'Rakshasa', lord: 'Mars', yoni: 'Tiger' },
  'Swati': { gana: 'Deva', lord: 'Rahu', yoni: 'Buffalo' },
  'Vishakha': { gana: 'Rakshasa', lord: 'Jupiter', yoni: 'Tiger' },
  'Anuradha': { gana: 'Deva', lord: 'Saturn', yoni: 'Deer' },
  'Jyeshtha': { gana: 'Rakshasa', lord: 'Mercury', yoni: 'Deer' },
  'Mula': { gana: 'Rakshasa', lord: 'Ketu', yoni: 'Dog' },
  'Purva Ashadha': { gana: 'Manushya', lord: 'Venus', yoni: 'Monkey' },
  'Uttara Ashadha': { gana: 'Manushya', lord: 'Sun', yoni: 'Mongoose' },
  'Shravana': { gana: 'Deva', lord: 'Moon', yoni: 'Monkey' },
  'Dhanishta': { gana: 'Rakshasa', lord: 'Mars', yoni: 'Lion' },
  'Shatabhisha': { gana: 'Rakshasa', lord: 'Rahu', yoni: 'Horse' },
  'Purva Bhadrapada': { gana: 'Manushya', lord: 'Jupiter', yoni: 'Lion' },
  'Uttara Bhadrapada': { gana: 'Manushya', lord: 'Saturn', yoni: 'Cow' },
  'Revati': { gana: 'Deva', lord: 'Mercury', yoni: 'Elephant' },
};

// ===== NAKSHATRA NAME NORMALIZATION =====

/**
 * Normalize nakshatra names to handle different formats and spellings:
 * - "PurvaBhadrapada" -> "Purva Bhadrapada"
 * - "Purva-Bhadrapada" -> "Purva Bhadrapada"
 * - "UttaraBhadrapada" -> "Uttara Bhadrapada"
 * - "Poorva Phalguni" -> "Purva Phalguni"
 * - Handle common spelling variations
 */
function normalizeNakshatraName(nakshatra: string): string {
  if (!nakshatra) return '';
  
  // Helper to remove diacritical marks and normalize to ASCII
  const removeDiacritics = (str: string): string => {
    return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  };
  
  // Create mapping of common variations to canonical names
  const nameMap: Record<string, string> = {
    // Common spelling variations
    'Poorva Phalguni': 'Purva Phalguni',
    'PoorvaPhalguni': 'Purva Phalguni',
    'Poorva Ashadha': 'Purva Ashadha',
    'PoorvaAshadha': 'Purva Ashadha',
    'Poorva Bhadrapada': 'Purva Bhadrapada',
    'PoortaBhadrapada': 'Purva Bhadrapada',
    'Uttara Phalguni': 'Uttara Phalguni',
    'UttaraPhalguni': 'Uttara Phalguni',
    'Uttaraphalguni': 'Uttara Phalguni',
    'Uttara Ashadha': 'Uttara Ashadha',
    'UttaraAshadha': 'Uttara Ashadha',
    'Uttarashadha': 'Uttara Ashadha',
    'Uttara Bhadrapada': 'Uttara Bhadrapada',
    'UttaraBhadrapada': 'Uttara Bhadrapada',
    // Compound names without spaces
    'PurvaPhalguni': 'Purva Phalguni',
    'PurvaAshadha': 'Purva Ashadha',
    'PurvaBhadrapada': 'Purva Bhadrapada',
    // Mrigashira variations
    'Mrigashirsha': 'Mrigashira',
    'Mrigasira': 'Mrigashira',
    'Mrigasirsha': 'Mrigashira',
    // Dhanishta variations
    'Dhanishtha': 'Dhanishta',
    'Dhanistha': 'Dhanishta',
    // Ashlesha variations
    'Aslesha': 'Ashlesha',
    // Punarvasu variations
    'Punarvasu': 'Punarvasu',
    // Pushya variations
    'Pushyami': 'Pushya',
    'Pushyam': 'Pushya',
    'Pooshyami': 'Pushya',
    // Other variations
    'Ashwini': 'Ashwini',
    'Aswini': 'Ashwini',
    'Krittika': 'Krittika',
    'Kritika': 'Krittika',
  };
  
  // First check direct mapping
  if (nameMap[nakshatra]) {
    return nameMap[nakshatra];
  }
  
  // Strip diacritics and try again
  const noDiacritics = removeDiacritics(nakshatra);
  if (nameMap[noDiacritics]) {
    return nameMap[noDiacritics];
  }
  
  // Handle Purva/Uttara compound names with regex
  let normalized = noDiacritics
    .replace(/Purva([A-Z])/gi, 'Purva $1')
    .replace(/Uttara([A-Z])/gi, 'Uttara $1')
    .replace(/Poorva([A-Z])/gi, 'Purva $1')
    .replace(/Ashadha/gi, 'Ashadha')
    .replace(/shadha/gi, 'shadha')
    .replace(/-/g, ' ')
    .trim();
  
  // Capitalize first letters properly
  normalized = normalized.split(' ').map(word => 
    word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
  ).join(' ');
  
  // Check if normalized name exists in NAKSHATRA_DATA
  if (NAKSHATRA_DATA[normalized]) {
    return normalized;
  }
  
  // Try original name
  if (NAKSHATRA_DATA[nakshatra]) {
    return nakshatra;
  }
  
  // Try without diacritics
  if (NAKSHATRA_DATA[noDiacritics]) {
    return noDiacritics;
  }
  
  // Last resort: return normalized version (might still be unknown)
  console.warn(`[Marriage Compatibility] Unknown nakshatra: "${nakshatra}" normalized to "${normalized}"`);
  return normalized;
}

// ===== PLANETARY RELATIONSHIPS =====

// Natural malefics for affliction analysis (universal, not lagna-specific)
const NATURAL_MALEFICS: PlanetName[] = ['Sun', 'Mars', 'Saturn', 'Rahu', 'Ketu'];

const PLANETARY_FRIENDS: Record<PlanetName, PlanetName[]> = {
  Sun: ['Moon', 'Mars', 'Jupiter'],
  Moon: ['Sun', 'Mercury'],
  Mars: ['Sun', 'Moon', 'Jupiter'],
  Mercury: ['Sun', 'Venus'],
  Jupiter: ['Sun', 'Moon', 'Mars'],
  Venus: ['Mercury', 'Saturn'],
  Saturn: ['Mercury', 'Venus'],
  Rahu: [],
  Ketu: [],
};

const PLANETARY_ENEMIES: Record<PlanetName, PlanetName[]> = {
  Sun: ['Venus', 'Saturn'],
  Moon: [],
  Mars: ['Mercury'],
  Mercury: ['Moon'],
  Jupiter: ['Venus'],
  Venus: ['Sun', 'Moon'],
  Saturn: ['Sun', 'Moon', 'Mars'],
  Rahu: [],
  Ketu: [],
};

// ===== YONI RELATIONSHIPS =====

const YONI_FRIENDS: Record<string, string[]> = {
  'Horse': ['Mare', 'Elephant'],
  'Elephant': ['Horse', 'Mare'],
  'Goat': ['Monkey', 'Deer'],
  'Serpent': ['Mongoose'],
  'Dog': ['Rabbit'],
  'Cat': ['Rat'],
  'Rat': ['Cat'],
  'Cow': ['Buffalo'],
  'Buffalo': ['Cow', 'Tiger'],
  'Tiger': ['Buffalo'],
  'Deer': ['Goat', 'Monkey'],
  'Monkey': ['Deer', 'Goat'],
  'Lion': ['Mare'],
  'Mare': ['Horse', 'Lion'],
  'Mongoose': ['Serpent'],
  'Rabbit': ['Dog'],
};

const YONI_ENEMIES: Record<string, string[]> = {
  'Mongoose': ['Serpent'],
  'Serpent': ['Mongoose'],
  'Dog': ['Deer'],
  'Deer': ['Dog'],
  'Cat': ['Rat'],
  'Rat': ['Cat'],
  'Lion': ['Elephant'],
  'Elephant': ['Lion'],
  'Horse': ['Buffalo'],
  'Buffalo': ['Horse'],
  'Cow': ['Tiger'],
  'Tiger': ['Cow'],
};

// ===== UTILITY FUNCTIONS =====

function getSignDistance(sign1: number, sign2: number): string {
  const dist = ((sign2 - sign1 + 12) % 12) + 1;
  const reverseDist = ((sign1 - sign2 + 12) % 12) + 1;
  
  // Return canonical representation (lower number first)
  if (dist <= reverseDist) {
    return `${dist}-${reverseDist}`;
  } else {
    return `${reverseDist}-${dist}`;
  }
}

function getPlanetaryRelationship(planet1: PlanetName, planet2: PlanetName): 'same' | 'friendly' | 'neutral' | 'enemy' {
  if (planet1 === planet2) return 'same';
  if (PLANETARY_FRIENDS[planet1]?.includes(planet2)) return 'friendly';
  if (PLANETARY_ENEMIES[planet1]?.includes(planet2)) return 'enemy';
  return 'neutral';
}

function getYoniRelationship(yoni1: string, yoni2: string): 'same' | 'friendly' | 'neutral' | 'enemy' {
  if (yoni1 === yoni2) return 'same';
  if (YONI_FRIENDS[yoni1]?.includes(yoni2)) return 'friendly';
  if (YONI_ENEMIES[yoni1]?.includes(yoni2)) return 'enemy';
  return 'neutral';
}

/**
 * Check if a planet is functionally benefic or malefic for a given lagna
 * Returns the planet's functional nature: 'benefic', 'neutral', or 'malefic'
 */
function getFunctionalNature(planet: PlanetName, lagna: number): 'benefic' | 'neutral' | 'malefic' {
  const funcMap = classifyFunctionalForLagna(lagna) as Record<PlanetName, 'benefic' | 'neutral' | 'malefic'>;
  return funcMap[planet] || 'neutral';
}

// ===== ASHTAKUTA HELPER DATA =====

const NAKSHATRA_TO_VARNA: Record<string, 'Brahmin' | 'Kshatriya' | 'Vaishya' | 'Shudra'> = {
  'Ashwini': 'Vaishya', 'Bharani': 'Vaishya', 'Krittika': 'Brahmin', 'Rohini': 'Shudra',
  'Mrigashira': 'Vaishya', 'Ardra': 'Shudra', 'Punarvasu': 'Vaishya', 'Pushya': 'Kshatriya',
  'Ashlesha': 'Vaishya', 'Magha': 'Shudra', 'Purva Phalguni': 'Brahmin', 'Uttara Phalguni': 'Kshatriya',
  'Hasta': 'Vaishya', 'Chitra': 'Vaishya', 'Swati': 'Shudra', 'Vishakha': 'Vaishya',
  'Anuradha': 'Shudra', 'Jyeshtha': 'Vaishya', 'Mula': 'Shudra', 'Purva Ashadha': 'Brahmin',
  'Uttara Ashadha': 'Kshatriya', 'Shravana': 'Vaishya', 'Dhanishta': 'Vaishya', 'Shatabhisha': 'Shudra',
  'Purva Bhadrapada': 'Brahmin', 'Uttara Bhadrapada': 'Kshatriya', 'Revati': 'Shudra',
};

const NAKSHATRA_TO_VASHYA: Record<string, 'Chatushpada' | 'Manava' | 'Jalachara' | 'Vanachara' | 'Kita'> = {
  'Ashwini': 'Chatushpada', 'Bharani': 'Manava', 'Krittika': 'Chatushpada', 'Rohini': 'Chatushpada',
  'Mrigashira': 'Chatushpada', 'Ardra': 'Manava', 'Punarvasu': 'Manava', 'Pushya': 'Chatushpada',
  'Ashlesha': 'Jalachara', 'Magha': 'Chatushpada', 'Purva Phalguni': 'Manava', 'Uttara Phalguni': 'Chatushpada',
  'Hasta': 'Chatushpada', 'Chitra': 'Kita', 'Swati': 'Chatushpada', 'Vishakha': 'Chatushpada',
  'Anuradha': 'Kita', 'Jyeshtha': 'Jalachara', 'Mula': 'Chatushpada', 'Purva Ashadha': 'Manava',
  'Uttara Ashadha': 'Chatushpada', 'Shravana': 'Chatushpada', 'Dhanishta': 'Chatushpada', 'Shatabhisha': 'Jalachara',
  'Purva Bhadrapada': 'Manava', 'Uttara Bhadrapada': 'Manava', 'Revati': 'Jalachara',
};

const NAKSHATRA_TO_NADI: Record<string, 'Vata' | 'Pitta' | 'Kapha'> = {
  // Vata (Aadi) - 1, 10, 19 sequence
  'Ashwini': 'Vata', 'Ardra': 'Vata', 'Punarvasu': 'Vata', 'Uttara Phalguni': 'Vata',
  'Hasta': 'Vata', 'Jyeshtha': 'Vata', 'Mula': 'Vata', 'Shatabhisha': 'Vata', 'Purva Bhadrapada': 'Vata',
  
  // Pitta (Madhya) - 2, 11, 20 sequence
  'Bharani': 'Pitta', 'Mrigashira': 'Pitta', 'Pushya': 'Pitta', 'Purva Phalguni': 'Pitta',
  'Chitra': 'Pitta', 'Anuradha': 'Pitta', 'Purva Ashadha': 'Pitta', 'Dhanishta': 'Pitta', 'Uttara Bhadrapada': 'Pitta',
  
  // Kapha (Antya) - 3, 12, 21 sequence
  'Krittika': 'Kapha', 'Rohini': 'Kapha', 'Ashlesha': 'Kapha', 'Magha': 'Kapha',
  'Swati': 'Kapha', 'Vishakha': 'Kapha', 'Uttara Ashadha': 'Kapha', 'Shravana': 'Kapha', 'Revati': 'Kapha',
};

// ===== ANALYSIS FUNCTIONS =====

export function analyzeMoonDistance(person1: PartnerChart, person2: PartnerChart): MoonDistanceAnalysis {
  const distance = getSignDistance(person1.moonSign, person2.moonSign);
  
  const distanceMap: Record<string, { name: string; meaning: string; outcome: string; score: 'high' | 'moderate' | 'low' | 'conditional'; percent: number }> = {
    '1-1': {
      name: 'Same sign',
      meaning: 'Same emotional wavelength',
      outcome: 'Emotionally bonded, requires emotional maturity to avoid power struggles',
      score: 'high',
      percent: 85
    },
    '2-12': {
      name: 'Dwi-dwadasa',
      meaning: 'Support vs drain',
      outcome: 'Supportive but asymmetrical - one partner gives more emotionally',
      score: 'conditional',
      percent: 60
    },
    '3-11': {
      name: 'Trikona growth axis',
      meaning: 'Stimulation + irritation',
      outcome: 'Exciting, effort-based emotional bond - keeps relationship dynamic',
      score: 'moderate',
      percent: 70
    },
    '4-10': {
      name: 'Kendra tension',
      meaning: 'Duty vs comfort',
      outcome: 'Functional but emotionally dry if neglected - needs conscious nurturing',
      score: 'moderate',
      percent: 65
    },
    '5-9': {
      name: 'Dharma harmony',
      meaning: 'Natural understanding',
      outcome: 'Emotionally harmonious and supportive - natural flow of feelings',
      score: 'high',
      percent: 90
    },
    '6-8': {
      name: 'Shadashtaka',
      meaning: 'Emotional incompatibility',
      outcome: 'Emotionally challenging, requires conscious work and therapy',
      score: 'low',
      percent: 40
    },
  };
  
  const data = distanceMap[distance] || distanceMap['1-1'];
  
  return {
    distance,
    relationshipName: data.name,
    meaning: data.meaning,
    outcome: data.outcome,
    score: data.score,
    compatibilityPercent: data.percent,
  };
}

export function analyzeGanaCompatibility(person1: PartnerChart, person2: PartnerChart): GanaCompatibility {
  const nak1 = normalizeNakshatraName(person1.moonNakshatra);
  const nak2 = normalizeNakshatraName(person2.moonNakshatra);
  const gana1 = NAKSHATRA_DATA[nak1]?.gana || 'Manushya';
  const gana2 = NAKSHATRA_DATA[nak2]?.gana || 'Manushya';
  
  const combo = `${gana1}–${gana2}`;
  
  const ganaMap: Record<string, { outcome: string; adjustment: 'easy' | 'manageable' | 'requires-work' | 'major-adjustment'; percent: number }> = {
    'Deva–Deva': { outcome: 'Harmony, avoidance of conflict - Both gentle and idealistic', adjustment: 'easy', percent: 85 },
    'Manushya–Manushya': { outcome: 'Stable, realistic marriage - Both practical and worldly', adjustment: 'easy', percent: 80 },
    'Rakshasa–Rakshasa': { outcome: 'Power struggles - Both intense and dominant', adjustment: 'requires-work', percent: 60 },
    'Deva–Manushya': { outcome: 'Manageable - Idealist vs realist can balance each other', adjustment: 'manageable', percent: 75 },
    'Manushya–Deva': { outcome: 'Manageable - Realist vs idealist can balance each other', adjustment: 'manageable', percent: 75 },
    'Deva–Rakshasa': { outcome: 'Value clash - Gentle vs intense creates major adjustment', adjustment: 'major-adjustment', percent: 45 },
    'Rakshasa–Deva': { outcome: 'Value clash - Intense vs gentle creates major adjustment', adjustment: 'major-adjustment', percent: 45 },
    'Manushya–Rakshasa': { outcome: 'Recurrent conflicts - Control vs freedom dynamics', adjustment: 'requires-work', percent: 55 },
    'Rakshasa–Manushya': { outcome: 'Recurrent conflicts - Dominance vs practicality', adjustment: 'requires-work', percent: 55 },
  };
  
  const data = ganaMap[combo] || { outcome: 'Neutral compatibility', adjustment: 'manageable' as const, percent: 70 };
  
  return {
    person1Gana: gana1,
    person2Gana: gana2,
    combo,
    outcome: data.outcome,
    adjustment: data.adjustment,
    compatibilityPercent: data.percent,
  };
}

export function analyzeNakshatraLordCompatibility(person1: PartnerChart, person2: PartnerChart): NakshatraLordCompatibility {
  const lord1 = person1.moonNakshatraLord;
  const lord2 = person2.moonNakshatraLord;
  const relationship = getPlanetaryRelationship(lord1, lord2);
  
  const outcomeMap: Record<string, { outcome: string; percent: number }> = {
    'same': { outcome: 'High instinctive bonding - Same psychological wavelength', percent: 90 },
    'friendly': { outcome: 'Smooth adjustment - Natural affinity between minds', percent: 80 },
    'neutral': { outcome: 'Effort-based - Requires conscious understanding', percent: 65 },
    'enemy': { outcome: 'Psychological friction - Different mental operating systems', percent: 50 },
  };
  
  const data = outcomeMap[relationship];
  
  return {
    person1Lord: lord1,
    person2Lord: lord2,
    relationship,
    outcome: data.outcome,
    compatibilityPercent: data.percent,
  };
}

export function analyzeYoniCompatibility(person1: PartnerChart, person2: PartnerChart): YoniCompatibility {
  const nak1 = normalizeNakshatraName(person1.moonNakshatra);
  const nak2 = normalizeNakshatraName(person2.moonNakshatra);
  const yoni1 = NAKSHATRA_DATA[nak1]?.yoni || 'Unknown';
  const yoni2 = NAKSHATRA_DATA[nak2]?.yoni || 'Unknown';
  const relationship = getYoniRelationship(yoni1, yoni2);
  
  const outcomeMap: Record<string, { outcome: string; percent: number }> = {
    'same': { outcome: 'Natural attraction - Same instinctual drives', percent: 90 },
    'friendly': { outcome: 'Healthy chemistry - Compatible physical expression', percent: 80 },
    'neutral': { outcome: 'Workable - No major instinctual conflicts', percent: 70 },
    'enemy': { outcome: 'Sexual mismatch, dominance issues - Different physical needs', percent: 45 },
  };
  
  const data = outcomeMap[relationship];
  
  return {
    person1Yoni: yoni1,
    person2Yoni: yoni2,
    relationship,
    outcome: data.outcome,
    compatibilityPercent: data.percent,
  };
}

export function analyzeLagnaCompatibility(person1: PartnerChart, person2: PartnerChart): LagnaCompatibility {
  const distance = getSignDistance(person1.lagna, person2.lagna);
  
  const distanceMap: Record<string, { outcome: string; flags: string[]; percent: number }> = {
    '1-1': { outcome: 'Similar lifestyle, ego clashes - Both want to lead', flags: ['Competition risk', 'Similar needs'], percent: 70 },
    '1-7': { outcome: 'Strong attraction - Magnetic but volatile', flags: ['Intense pull', 'Opposition dynamics'], percent: 75 },
    '2-12': { outcome: 'Support vs sacrifice imbalance - One gives more', flags: ['Asymmetrical support'], percent: 60 },
    '3-11': { outcome: 'Growth-oriented - Stimulating partnership', flags: ['Dynamic growth'], percent: 75 },
    '4-10': { outcome: 'Duty focus - Career vs home tensions', flags: ['Work-life balance needed'], percent: 65 },
    '5-9': { outcome: 'Shared values - Natural dharma alignment', flags: ['Spiritual harmony'], percent: 85 },
    '6-8': { outcome: 'Power struggle - Ego friction', flags: ['Major ego conflicts', 'Control issues'], percent: 45 },
  };
  
  const data = distanceMap[distance] || { outcome: 'Neutral compatibility', flags: [], percent: 70 };
  
  return {
    distance,
    outcome: data.outcome,
    flags: data.flags,
    compatibilityPercent: data.percent,
  };
}

export function analyzeSeventhHouse(person: PartnerChart): SeventhHouseAnalysis {
  const occupantOutcomes: Record<PlanetName, string> = {
    Venus: 'Love-oriented - Romantic marriage focus',
    Jupiter: 'Ethical, protective - Stability through wisdom',
    Moon: 'Emotional dependency - Mood-based marriage',
    Mercury: 'Communication-based - Business-like partnership',
    Mars: 'Conflict, passion - Arguments + attraction cycles',
    Saturn: 'Delay, duty - Longevity with effort',
    Rahu: 'Unconventional - Inter-caste/foreign spouse',
    Ketu: 'Detachment - Distance phases in marriage',
    Sun: 'Ego in partnership - Dominance issues',
  };
  
  const occupants = person.planetsInH7.map(planet => ({
    planet,
    outcome: occupantOutcomes[planet] || 'Influences marriage dynamics',
  }));
  
  // Check for Jupiter aspect on 7th house - major protective factor
  const jupiterProtection = person.aspectingH7.includes('Jupiter');
  
  // Analyze 7th lord condition
  const { seventhLordState } = person;
  
  // Check if 7th lord is afflicted by NATURAL malefics (universal principle for marriage)
  const isAfflicted = person.seventhLordAspects?.some(planet => 
    NATURAL_MALEFICS.includes(planet)
  ) || false;
  
  // Check if 7th lord itself is functionally benefic or malefic for the lagna
  const seventhLordNature = getFunctionalNature(person.seventhLord, person.lagna);
  const isFunctionallyBenefic = seventhLordNature === 'benefic';
  
  let state = '';
  let impact = '';
  
  if (seventhLordState.exalted || seventhLordState.ownSign) {
    state = 'Strong (Exalted/Own Sign)';
    impact = 'Strong marriage support - Natural ability to sustain partnership';
  } else if (seventhLordState.debilitated) {
    state = 'Debilitated';
    impact = 'Weak commitment - Struggles in marriage sustenance';
  } else if (seventhLordState.combust) {
    state = 'Combust';
    impact = 'Ego burns relationship - Pride interferes with partnership';
  } else if (isAfflicted) {
    state = 'Afflicted by natural malefics';
    impact = 'Stress cycles - Marriage faces recurring challenges from external pressures';
  } else if (seventhLordState.retrograde) {
    state = 'Retrograde';
    impact = 'Rewriting marriage rules - Unconventional approach to partnership';
  } else if ([6, 8, 12].includes(person.seventhLordHouse)) {
    state = `In ${person.seventhLordHouse}th house`;
    impact = 'Conflict/separation themes - Marriage faces obstacles';
  } else if (isFunctionallyBenefic) {
    state = 'Functionally benefic for lagna';
    impact = 'Good adjustment - Supportive marriage dynamics';
  } else {
    state = 'Functional';
    impact = 'Good adjustment - Marriage functions adequately';
  }
  
  return {
    person: person.name,
    occupants,
    jupiterProtection,
    seventhLordCondition: {
      lord: person.seventhLord,
      state,
      impact,
    },
  };
}

export function analyzeVenusJupiter(person: PartnerChart): VenusJupiterAnalysis {
  const { venus, jupiter } = person;
  
  // Venus analysis
  let venusState = '';
  let venusOutcome = '';
  let venusStrength: 'strong' | 'moderate' | 'weak' = 'moderate';
  
  if (venus.state.exalted || venus.state.ownSign) {
    venusState = 'Strong (Exalted/Own Sign)';
    venusOutcome = 'Ability to love & adjust - Natural romantic capacity';
    venusStrength = 'strong';
  } else if (venus.state.combust) {
    venusState = 'Combust';
    venusOutcome = 'Love ego clashes - Pride interferes with affection';
    venusStrength = 'weak';
  } else if (venus.state.debilitated) {
    venusState = 'Debilitated';
    venusOutcome = 'Emotional dissatisfaction - Struggles with love expression';
    venusStrength = 'weak';
  } else if (venus.state.retrograde) {
    venusState = 'Retrograde';
    venusOutcome = 'Re-evaluates love - Unconventional romantic needs';
    venusStrength = 'moderate';
  } else {
    venusState = 'Functional';
    venusOutcome = 'Adequate love capacity - Works with effort';
    venusStrength = 'moderate';
  }
  
  // Jupiter analysis
  let jupiterState = '';
  let jupiterOutcome = '';
  let jupiterStrength: 'strong' | 'moderate' | 'weak' = 'moderate';
  
  if (jupiter.state.exalted || jupiter.state.ownSign) {
    jupiterState = 'Strong (Exalted/Own Sign)';
    jupiterOutcome = 'Protection of marriage - Wisdom sustains relationship';
    jupiterStrength = 'strong';
  } else if (jupiter.state.debilitated) {
    jupiterState = 'Debilitated';
    jupiterOutcome = 'Poor decision-making - Lacks marital wisdom';
    jupiterStrength = 'weak';
  } else if (jupiter.state.retrograde) {
    jupiterState = 'Retrograde';
    jupiterOutcome = 'Late maturity - Wisdom comes through experience';
    jupiterStrength = 'moderate';
  } else {
    jupiterState = 'Functional';
    jupiterOutcome = 'Adequate wisdom - Functions with guidance';
    jupiterStrength = 'moderate';
  }
  
  // Overall capacity
  let overallCapacity = '';
  if (venusStrength === 'weak' && jupiterStrength === 'weak') {
    overallCapacity = '⚠️ Marriage survives only through effort, not ease - Both love and wisdom challenged';
  } else if (venusStrength === 'strong' && jupiterStrength === 'strong') {
    overallCapacity = '✅ Strong capacity for love and sustaining marriage - Natural relationship skills';
  } else {
    overallCapacity = '➖ Mixed capacity - One area strong, other needs development';
  }
  
  return {
    person: person.name,
    venus: {
      state: venusState,
      outcome: venusOutcome,
      strength: venusStrength,
    },
    jupiter: {
      state: jupiterState,
      outcome: jupiterOutcome,
      strength: jupiterStrength,
    },
    overallCapacity,
  };
}

/**
 * Analyze Manglik Dosha (Kuja Dosha) - Mars placement in certain houses
 * causes marital issues, but can be cancelled by various factors.
 */
export function analyzeD9Compatibility(
  person1: PartnerChart,
  person2: PartnerChart
): D9Analysis | undefined {
  // Check if both charts have D9 data
  if (!person1.d9 || !person2.d9) {
    return undefined;
  }

  const benefics: PlanetName[] = ['Jupiter', 'Venus', 'Mercury', 'Moon'];
  const malefics: PlanetName[] = ['Sun', 'Mars', 'Saturn', 'Rahu', 'Ketu'];

  // Helper: Get D9 H1 planet meaning
  const getD9H1PlanetMeaning = (planet: PlanetName): string => {
    const meanings: Record<PlanetName, string> = {
      Sun: 'Strong ego, leadership role in marriage',
      Moon: 'Emotionally driven spouse, nurturing identity',
      Mars: 'Assertive, sometimes confrontational approach',
      Mercury: 'Communicative, adaptable marital identity',
      Jupiter: 'Mature, ethical, guiding role in marriage',
      Venus: 'Relationship-centric identity, spouse as priority',
      Saturn: 'Serious, duty-bound, karmic responsibility',
      Rahu: 'Unconventional marital identity, breaks norms',
      Ketu: 'Detached or inward-focused, spiritual approach',
    };
    return meanings[planet] || 'Neutral influence';
  };

  // Helper: Get D9 H7 planet tone (marriage environment)
  const getD9H7PlanetTone = (planet: PlanetName): string => {
    const tones: Record<PlanetName, string> = {
      Venus: 'Love-focused, romantic, harmonious',
      Jupiter: 'Protective, wise, expansive marriage',
      Moon: 'Emotional bonding, nurturing connection',
      Mars: 'Conflict-prone, passionate, energetic',
      Saturn: 'Delay, duty, karmic obligations',
      Rahu: 'Non-traditional, unconventional setup',
      Ketu: 'Distance phases, spiritual detachment',
      Sun: 'Partnership ego, authority dynamics',
      Mercury: 'Communication-based, intellectual bond',
    };
    return tones[planet] || 'Neutral marriage tone';
  };

  // Helper: Calculate H1 influence (benefic vs malefic dominant)
  const calculateH1Influence = (planetsInH1: PlanetName[]): 'benefic-dominant' | 'malefic-dominant' | 'mixed' => {
    const beneficCount = planetsInH1.filter(p => benefics.includes(p)).length;
    const maleficCount = planetsInH1.filter(p => malefics.includes(p)).length;
    
    if (beneficCount > maleficCount) return 'benefic-dominant';
    if (maleficCount > beneficCount) return 'malefic-dominant';
    return 'mixed';
  };

  // Helper: Calculate Lagna lord strength from state
  const calculateLagnaLordStrength = (state: {
    exalted: boolean;
    debilitated: boolean;
    ownSign: boolean;
    friendly: boolean;
    combust: boolean;
    retrograde: boolean;
  }): 'strong' | 'weak' | 'moderate' => {
    if (state.exalted || state.ownSign) return 'strong';
    if (state.debilitated || (state.combust && !state.exalted)) return 'weak';
    return 'moderate';
  };

  // Helper: Get Lagna lord outcome based on state and H1 influence
  const getLagnaLordOutcome = (
    state: {
      exalted: boolean;
      debilitated: boolean;
      ownSign: boolean;
      friendly: boolean;
      combust: boolean;
      retrograde: boolean;
    },
    h1Influence: 'benefic-dominant' | 'malefic-dominant' | 'mixed'
  ): string => {
    let baseOutcome = '';
    
    if (state.exalted) {
      baseOutcome = 'Strong post-marriage stability, empowered identity';
    } else if (state.ownSign) {
      baseOutcome = 'Natural self-confidence in marital role';
    } else if (state.debilitated) {
      baseOutcome = 'Identity insecurity in marriage, needs support';
    } else if (state.combust) {
      baseOutcome = 'Ego wounds spill into marriage, self-esteem issues';
    } else if (state.retrograde) {
      baseOutcome = 'Rewriting self-image post-marriage, internal transformation';
    } else if (state.friendly) {
      baseOutcome = 'Smooth adaptation to married life';
    } else {
      baseOutcome = 'Moderate adjustment to marital identity';
    }

    // Add H1 influence context
    if (h1Influence === 'benefic-dominant') {
      baseOutcome += ' — Willingness to grow and adapt';
    } else if (h1Influence === 'malefic-dominant') {
      baseOutcome += ' — Defensive ego, resistance to change';
    } else {
      baseOutcome += ' — Growth through struggle and challenge';
    }

    return baseOutcome;
  };

  // Helper: Calculate H1-H7 balance pattern
  const calculateBalancePattern = (
    lagnaLordStrength: 'strong' | 'weak' | 'moderate',
    h1Influence: 'benefic-dominant' | 'malefic-dominant' | 'mixed',
    seventhLordCondition: 'strong' | 'weak' | 'afflicted',
    beneficsInH7: PlanetName[],
    maleficsInH7: PlanetName[]
  ): {
    h1Strength: 'strong' | 'weak';
    h7Strength: 'strong' | 'weak';
    balancePattern: 'strong-strong' | 'strong-weak' | 'weak-strong' | 'weak-weak';
    balanceOutcome: string;
  } => {
    // H1 strength: More nuanced evaluation
    // Strong: (Lagna lord strong) OR (Lagna lord moderate + benefic-dominant)
    // Weak: (Lagna lord weak) OR (Lagna lord moderate + malefic-dominant)
    const h1Strength: 'strong' | 'weak' = 
      (lagnaLordStrength === 'strong') || 
      (lagnaLordStrength === 'moderate' && h1Influence === 'benefic-dominant')
        ? 'strong' 
        : 'weak';
    
    // H7 strength: strong if 7th lord is strong OR (7th lord moderate + more benefics)
    // More lenient: H7 can be strong even with empty house if 7th lord is strong
    const h7Strength: 'strong' | 'weak' = 
      (seventhLordCondition === 'strong') || 
      (seventhLordCondition !== 'weak' && beneficsInH7.length > maleficsInH7.length)
        ? 'strong' 
        : 'weak';
    
    // Determine pattern
    let balancePattern: 'strong-strong' | 'strong-weak' | 'weak-strong' | 'weak-weak';
    let balanceOutcome: string;
    
    if (h1Strength === 'strong' && h7Strength === 'strong') {
      balancePattern = 'strong-strong';
      balanceOutcome = 'Natural, resilient marriage — Both identity and partnership are well-supported';
    } else if (h1Strength === 'strong' && h7Strength === 'weak') {
      balancePattern = 'strong-weak';
      balanceOutcome = 'Person carries the marriage — May feel burdened by partner\'s needs';
    } else if (h1Strength === 'weak' && h7Strength === 'strong') {
      balancePattern = 'weak-strong';
      balanceOutcome = 'Marriage overwhelms person — Identity loss, self sacrificed for partnership';
    } else {
      balancePattern = 'weak-weak';
      balanceOutcome = 'High burnout risk — Both struggle, neither has strong foundation';
    }
    
    return { h1Strength, h7Strength, balancePattern, balanceOutcome };
  };

  // Person 1 D9 H1 Analysis
  const p1PlanetsInD9H1WithMeanings = person1.d9.planetsInD9H1.map(planet => ({
    planet,
    meaning: getD9H1PlanetMeaning(planet),
  }));
  const p1H1Influence = calculateH1Influence(person1.d9.planetsInD9H1);
  const p1LagnaLordStrength = calculateLagnaLordStrength(person1.d9.lagnaLordState);
  const p1LagnaLordOutcome = getLagnaLordOutcome(person1.d9.lagnaLordState, p1H1Influence);

  // Person 2 D9 H1 Analysis
  const p2PlanetsInD9H1WithMeanings = person2.d9.planetsInD9H1.map(planet => ({
    planet,
    meaning: getD9H1PlanetMeaning(planet),
  }));
  const p2H1Influence = calculateH1Influence(person2.d9.planetsInD9H1);
  const p2LagnaLordStrength = calculateLagnaLordStrength(person2.d9.lagnaLordState);
  const p2LagnaLordOutcome = getLagnaLordOutcome(person2.d9.lagnaLordState, p2H1Influence);

  // Person 1 D9 H7 Condition
  const p1BeneficsInD9H7 = person1.d9.planetsInD9H7.filter(p => benefics.includes(p));
  const p1MaleficsInD9H7 = person1.d9.planetsInD9H7.filter(p => malefics.includes(p));
  const p1PlanetsInD9H7WithTones = person1.d9.planetsInD9H7.map(planet => ({
    planet,
    tone: getD9H7PlanetTone(planet),
  }));
  
  let p1VenusStrength: 'strong' | 'weak' | 'moderate' = 'moderate';
  if (person1.d9.venusState.exalted || person1.d9.venusState.ownSign) {
    p1VenusStrength = 'strong';
  } else if (person1.d9.venusState.debilitated) {
    p1VenusStrength = 'weak';
  }

  const p1SeventhLordAfflicted = malefics.includes(person1.d9.d9SeventhLord) && p1MaleficsInD9H7.length > 0;
  const p1SeventhLordCondition: 'strong' | 'weak' | 'afflicted' = p1SeventhLordAfflicted ? 'afflicted' : 'strong';

  // Calculate P1 H1-H7 balance
  const p1Balance = calculateBalancePattern(
    p1LagnaLordStrength,
    p1H1Influence,
    p1SeventhLordCondition,
    p1BeneficsInD9H7,
    p1MaleficsInD9H7
  );

  let p1Outcome = '';
  if (p1BeneficsInD9H7.length > 0 && p1VenusStrength === 'strong') {
    p1Outcome = 'Marriage matures beautifully with emotional fulfillment';
  } else if (p1MaleficsInD9H7.length > 0 || p1SeventhLordAfflicted) {
    p1Outcome = 'Post-marriage adjustment period with stress cycles';
  } else if (p1VenusStrength === 'weak') {
    p1Outcome = 'Emotionally dry marriage - requires conscious nurturing';
  } else {
    p1Outcome = 'Moderate marriage quality - average fulfillment';
  }

  // Person 2 D9 Condition
  const p2BeneficsInD9H7 = person2.d9.planetsInD9H7.filter(p => benefics.includes(p));
  const p2MaleficsInD9H7 = person2.d9.planetsInD9H7.filter(p => malefics.includes(p));
  const p2PlanetsInD9H7WithTones = person2.d9.planetsInD9H7.map(planet => ({
    planet,
    tone: getD9H7PlanetTone(planet),
  }));
  
  let p2VenusStrength: 'strong' | 'weak' | 'moderate' = 'moderate';
  if (person2.d9.venusState.exalted || person2.d9.venusState.ownSign) {
    p2VenusStrength = 'strong';
  } else if (person2.d9.venusState.debilitated) {
    p2VenusStrength = 'weak';
  }

  const p2SeventhLordAfflicted = malefics.includes(person2.d9.d9SeventhLord) && p2MaleficsInD9H7.length > 0;
  const p2SeventhLordCondition: 'strong' | 'weak' | 'afflicted' = p2SeventhLordAfflicted ? 'afflicted' : 'strong';

  // Calculate P2 H1-H7 balance
  const p2Balance = calculateBalancePattern(
    p2LagnaLordStrength,
    p2H1Influence,
    p2SeventhLordCondition,
    p2BeneficsInD9H7,
    p2MaleficsInD9H7
  );

  let p2Outcome = '';
  if (p2BeneficsInD9H7.length > 0 && p2VenusStrength === 'strong') {
    p2Outcome = 'Marriage matures beautifully with emotional fulfillment';
  } else if (p2MaleficsInD9H7.length > 0 || p2SeventhLordAfflicted) {
    p2Outcome = 'Post-marriage adjustment period with stress cycles';
  } else if (p2VenusStrength === 'weak') {
    p2Outcome = 'Emotionally dry marriage - requires conscious nurturing';
  } else {
    p2Outcome = 'Moderate marriage quality - average fulfillment';
  }

  // Cross-Chart Checks
  // Check if P1's Venus sign matches P2's D9 7th house sign
  const p2D9SeventhSign = ((person2.d9.lagnaSign - 1 + 6) % 12) + 1;
  const p1D9SeventhSign = ((person1.d9.lagnaSign - 1 + 6) % 12) + 1;
  
  console.log('[D9 Cross-Chart Debug]');
  console.log('P1 D9 Lagna:', person1.d9.lagnaSign, '7th house sign:', p1D9SeventhSign);
  console.log('P2 D9 Lagna:', person2.d9.lagnaSign, '7th house sign:', p2D9SeventhSign);
  console.log('P1 Venus D9 sign:', person1.d9.venusSign, 'P2 D9 7th sign:', p2D9SeventhSign);
  console.log('P2 Venus D9 sign:', person2.d9.venusSign, 'P1 D9 7th sign:', p1D9SeventhSign);
  console.log('P1 Mars D9 sign:', person1.d9.marsSign, 'P2 D9 7th sign:', p2D9SeventhSign);
  console.log('P2 Mars D9 sign:', person2.d9.marsSign, 'P1 D9 7th sign:', p1D9SeventhSign);
  console.log('P1 Lagna lord D9 sign:', person1.d9.lagnaLordSign, 'P2 D9 Lagna:', person2.d9.lagnaSign);
  console.log('P2 Lagna lord D9 sign:', person2.d9.lagnaLordSign, 'P1 D9 Lagna:', person1.d9.lagnaSign);
  
  // Exact sign matches
  const p1VenusInP2D9H7 = person1.d9.venusSign === p2D9SeventhSign;
  const p2VenusInP1D9H7 = person2.d9.venusSign === p1D9SeventhSign;
  
  // Venus aspects partner's D9 7th (7th aspect)
  const p1VenusAspectsP2D9H7 = ((person1.d9.venusSign - 1 + 6) % 12) + 1 === p2D9SeventhSign;
  const p2VenusAspectsP1D9H7 = ((person2.d9.venusSign - 1 + 6) % 12) + 1 === p1D9SeventhSign;
  
  // Check if P1's Lagna lord is in P2's D9 Lagna sign
  const p1LagnaLord = getSignLordInternal(person1.d9.lagnaSign);
  const p2LagnaLord = getSignLordInternal(person2.d9.lagnaSign);
  const p1LagnaLordInP2D9Lagna = person1.d9.lagnaLordSign === person2.d9.lagnaSign;
  const p2LagnaLordInP1D9Lagna = person2.d9.lagnaLordSign === person1.d9.lagnaSign;

  // Check if Mars sign matches partner's D9 7th house sign
  const p1MarsAfflictsP2D9H7 = person1.d9.marsSign === p2D9SeventhSign;
  const p2MarsAfflictsP1D9H7 = person2.d9.marsSign === p1D9SeventhSign;
  
  // Mars aspects partner's D9 7th (Mars has 4th, 7th, 8th aspects)
  const p1MarsAspectsP2D9H7 = [
    ((person1.d9.marsSign - 1 + 3) % 12) + 1,  // 4th aspect
    ((person1.d9.marsSign - 1 + 6) % 12) + 1,  // 7th aspect
    ((person1.d9.marsSign - 1 + 7) % 12) + 1   // 8th aspect
  ].includes(p2D9SeventhSign);
  const p2MarsAspectsP1D9H7 = [
    ((person2.d9.marsSign - 1 + 3) % 12) + 1,
    ((person2.d9.marsSign - 1 + 6) % 12) + 1,
    ((person2.d9.marsSign - 1 + 7) % 12) + 1
  ].includes(p1D9SeventhSign);
  
  // Same planet in same D9 sign (karmic connection)
  const bothVenusInSameSign = person1.d9.venusSign === person2.d9.venusSign;
  const bothMarsInSameSign = person1.d9.marsSign === person2.d9.marsSign;

  // Advanced D9 H1 (Lagna) overlays - planets in partner's identity house
  const p1LagnaLordInP2D9H1 = person1.d9.lagnaLordSign === person2.d9.lagnaSign;
  const p2LagnaLordInP1D9H1 = person2.d9.lagnaLordSign === person1.d9.lagnaSign;
  const p1VenusInP2D9H1 = person1.d9.venusSign === person2.d9.lagnaSign;
  const p2VenusInP1D9H1 = person2.d9.venusSign === person1.d9.lagnaSign;
  const p1SaturnInP2D9H1 = person1.d9.saturnSign === person2.d9.lagnaSign;
  const p2SaturnInP1D9H1 = person2.d9.saturnSign === person1.d9.lagnaSign;
  const p1MarsInP2D9H1 = person1.d9.marsSign === person2.d9.lagnaSign;
  const p2MarsInP1D9H1 = person2.d9.marsSign === person1.d9.lagnaSign;
  
  // ===== D1 (Birth Chart) OVERLAY CHECKS =====
  // Check if partner's planets fall in your D1 houses
  
  // Calculate D1 7th house sign for both
  const p1D1SeventhSign = ((person1.lagnaSign - 1 + 6) % 12) + 1;
  const p2D1SeventhSign = ((person2.lagnaSign - 1 + 6) % 12) + 1;
  
  // Calculate D1 4th house sign for both
  const p1D1FourthSign = ((person1.lagnaSign - 1 + 3) % 12) + 1;
  const p2D1FourthSign = ((person2.lagnaSign - 1 + 3) % 12) + 1;
  
  // D1 Overlay: Partner's Venus in your 7th house
  const p1VenusInP2D1H7 = person1.venusSign === p2D1SeventhSign;
  const p2VenusInP1D1H7 = person2.venusSign === p1D1SeventhSign;
  
  // D1 Overlay: Partner's Moon in your 4th house
  const p1MoonInP2D1H4 = person1.moonSign === p2D1FourthSign;
  const p2MoonInP1D1H4 = person2.moonSign === p1D1FourthSign;
  
  // D1 Overlay: Partner's Mars in your 7th house
  const p1MarsInP2D1H7 = person1.marsSign === p2D1SeventhSign;
  const p2MarsInP1D1H7 = person2.marsSign === p1D1SeventhSign;
  
  // D1 Overlay: Partner's Saturn in your 7th house
  const p1SaturnInP2D1H7 = person1.saturnSign === p2D1SeventhSign;
  const p2SaturnInP1D1H7 = person2.saturnSign === p1D1SeventhSign;
  
  // D1 Overlay: Partner's Rahu in your 7th house
  const p1RahuInP2D1H7 = person1.rahuSign === p2D1SeventhSign;
  const p2RahuInP1D1H7 = person2.rahuSign === p1D1SeventhSign;
  
  console.log('Cross-chart results:', {
    p1VenusInP2D9H7,
    p2VenusInP1D9H7,
    p1VenusAspectsP2D9H7,
    p2VenusAspectsP1D9H7,
    p1LagnaLordInP2D9Lagna,
    p2LagnaLordInP1D9Lagna,
    p1MarsAfflictsP2D9H7,
    p2MarsAfflictsP1D9H7,
    p1MarsAspectsP2D9H7,
    p2MarsAspectsP1D9H7,
    bothVenusInSameSign,
    bothMarsInSameSign
  });

  // Overall D9 Rating
  let points = 0;
  console.log('[D9 Rating Debug] Starting calculation for:', person1.name, 'and', person2.name);
  
  if (p1BeneficsInD9H7.length > 0) {
    points += 2;
    console.log(`  ✅ P1 benefics in D9 H7 (${p1BeneficsInD9H7.join(', ')}): +2 → Total: ${points}`);
  }
  
  if (p2BeneficsInD9H7.length > 0) {
    points += 2;
  }
  
  if (p1VenusStrength === 'strong') {
    points += 2;
    console.log(`  ✅ P1 Venus strong: +2 → Total: ${points}`);
  } else {
    console.log(`  ❌ P1 Venus NOT strong (${p1VenusStrength}): +0`);
  }
  
  if (p2VenusStrength === 'strong') {
    points += 2;
  }
  
  if (p1VenusInP2D9H7 || p2VenusInP1D9H7) {
    points += 3;
    console.log(`  ✅ Venus in opposite D9 H7: +3 → Total: ${points}`);
  }
  
  if (p1LagnaLordInP2D9Lagna || p2LagnaLordInP1D9Lagna) {
    points += 2;
    console.log(`  ✅ Lagna Lord in opposite D9 Lagna: +2 → Total: ${points}`);
  }
  
  if (p1MaleficsInD9H7.length > 0) {
    points -= 2;
    console.log(`  ⚠️ P1 malefics in D9 H7 (${p1MaleficsInD9H7.join(', ')}): -2 → Total: ${points}`);
  }
  
  if (p2MaleficsInD9H7.length > 0) {
    points -= 2;
  }
  
  if (p1MarsAfflictsP2D9H7 || p2MarsAfflictsP1D9H7) {
    points -= 3;
    console.log(`  ⚠️ Mars afflicts opposite D9 H7: -3 → Total: ${points}`);
  }
  
  console.log(`[D9 Rating Debug] Final points: ${points}`);

  let rating: 'excellent' | 'good' | 'fair' | 'challenging';
  if (points >= 8) rating = 'excellent';
  else if (points >= 5) rating = 'good';
  else if (points >= 2) rating = 'fair';
  else rating = 'challenging';

  console.log(`[D9 Rating Debug] Rating: ${rating} (${points} points)`);

  let interpretation = '';
  if (rating === 'excellent') {
    interpretation = 'D9 shows exceptional marriage potential - deep bonding and emotional fulfillment likely';
  } else if (rating === 'good') {
    interpretation = 'D9 indicates positive marriage reality - good foundation for long-term happiness';
  } else if (rating === 'fair') {
    interpretation = 'D9 shows mixed signals - marriage will require conscious effort and adjustments';
  } else {
    interpretation = 'D9 reveals challenges - focus on remedies and realistic expectations essential';
  }

  return {
    person1Name: person1.name,
    person2Name: person2.name,
    p1D9Condition: {
      planetsInD9H1: p1PlanetsInD9H1WithMeanings,
      lagnaLordStrength: p1LagnaLordStrength,
      lagnaLordOutcome: p1LagnaLordOutcome,
      h1Influence: p1H1Influence,
      planetsInD9H7: p1PlanetsInD9H7WithTones,
      beneficsInD9H7: p1BeneficsInD9H7,
      maleficsInD9H7: p1MaleficsInD9H7,
      venusStrength: p1VenusStrength,
      seventhLordCondition: p1SeventhLordCondition,
      seventhLordMeaning: p1SeventhLordAfflicted ? 'Testing relationship karma' : 'Stable marriage',
      h1Strength: p1Balance.h1Strength,
      h7Strength: p1Balance.h7Strength,
      balancePattern: p1Balance.balancePattern,
      balanceOutcome: p1Balance.balanceOutcome,
      outcome: p1Outcome,
    },
    p2D9Condition: {
      planetsInD9H1: p2PlanetsInD9H1WithMeanings,
      lagnaLordStrength: p2LagnaLordStrength,
      lagnaLordOutcome: p2LagnaLordOutcome,
      h1Influence: p2H1Influence,
      planetsInD9H7: p2PlanetsInD9H7WithTones,
      beneficsInD9H7: p2BeneficsInD9H7,
      maleficsInD9H7: p2MaleficsInD9H7,
      venusStrength: p2VenusStrength,
      seventhLordCondition: p2SeventhLordCondition,
      seventhLordMeaning: p2SeventhLordAfflicted ? 'Testing relationship karma' : 'Stable marriage',
      h1Strength: p2Balance.h1Strength,
      h7Strength: p2Balance.h7Strength,
      balancePattern: p2Balance.balancePattern,
      balanceOutcome: p2Balance.balanceOutcome,
      outcome: p2Outcome,
    },
    crossChartChecks: {
      p1VenusInP2D9H7,
      p2VenusInP1D9H7,
      p1VenusAspectsP2D9H7,
      p2VenusAspectsP1D9H7,
      p1LagnaLordInP2D9Lagna,
      p2LagnaLordInP1D9Lagna,
      p1MarsAfflictsP2D9H7,
      p2MarsAfflictsP1D9H7,
      p1MarsAspectsP2D9H7,
      p2MarsAspectsP1D9H7,
      bothVenusInSameSign,
      bothMarsInSameSign,
      // Advanced overlays - planets in partner's D9 H1 (Lagna/identity house)
      p1LagnaLordInP2D9H1: {
        present: p1LagnaLordInP2D9H1,
        meaning: p1LagnaLordInP2D9H1 ? 'Mutual growth — Person 1 supports Person 2\'s marital identity' : '',
      },
      p2LagnaLordInP1D9H1: {
        present: p2LagnaLordInP1D9H1,
        meaning: p2LagnaLordInP1D9H1 ? 'Mutual growth — Person 2 supports Person 1\'s marital identity' : '',
      },
      p1VenusInP2D9H1: {
        present: p1VenusInP2D9H1,
        meaning: p1VenusInP2D9H1 ? 'Spouse-centered identity — Person 2 defines self through Person 1\'s love' : '',
      },
      p2VenusInP1D9H1: {
        present: p2VenusInP1D9H1,
        meaning: p2VenusInP1D9H1 ? 'Spouse-centered identity — Person 1 defines self through Person 2\'s love' : '',
      },
      p1SaturnInP2D9H1: {
        present: p1SaturnInP2D9H1,
        meaning: p1SaturnInP2D9H1 ? 'Karmic obligation — Person 2 feels duty-bound to Person 1' : '',
      },
      p2SaturnInP1D9H1: {
        present: p2SaturnInP1D9H1,
        meaning: p2SaturnInP1D9H1 ? 'Karmic obligation — Person 1 feels duty-bound to Person 2' : '',
      },
      p1MarsInP2D9H1: {
        present: p1MarsInP2D9H1,
        meaning: p1MarsInP2D9H1 ? 'Ego clashes — Person 2 experiences identity friction from Person 1\'s assertiveness' : '',
      },
      p2MarsInP1D9H1: {
        present: p2MarsInP1D9H1,
        meaning: p2MarsInP1D9H1 ? 'Ego clashes — Person 1 experiences identity friction from Person 2\'s assertiveness' : '',
      },
    },
    d1Overlays: {
      p1VenusInP2D1H7: {
        present: p1VenusInP2D1H7,
        meaning: p1VenusInP2D1H7 ? `${person2.name} experiences ${person1.name} as spouse-like, charming, desirable` : '',
      },
      p2VenusInP1D1H7: {
        present: p2VenusInP1D1H7,
        meaning: p2VenusInP1D1H7 ? `${person1.name} experiences ${person2.name} as spouse-like, charming, desirable` : '',
      },
      p1MoonInP2D1H4: {
        present: p1MoonInP2D1H4,
        meaning: p1MoonInP2D1H4 ? `${person1.name} feels emotionally safe, familiar, homely to ${person2.name}` : '',
      },
      p2MoonInP1D1H4: {
        present: p2MoonInP1D1H4,
        meaning: p2MoonInP1D1H4 ? `${person2.name} feels emotionally safe, familiar, homely to ${person1.name}` : '',
      },
      p1MarsInP2D1H7: {
        present: p1MarsInP2D1H7,
        meaning: p1MarsInP2D1H7 ? `Day-to-day attraction + arguments between ${person1.name} and ${person2.name}` : '',
      },
      p2MarsInP1D1H7: {
        present: p2MarsInP1D1H7,
        meaning: p2MarsInP1D1H7 ? `Day-to-day attraction + arguments between ${person2.name} and ${person1.name}` : '',
      },
      p1SaturnInP2D1H7: {
        present: p1SaturnInP2D1H7,
        meaning: p1SaturnInP2D1H7 ? `Relationship feels serious, karmic, binding to ${person2.name}` : '',
      },
      p2SaturnInP1D1H7: {
        present: p2SaturnInP1D1H7,
        meaning: p2SaturnInP1D1H7 ? `Relationship feels serious, karmic, binding to ${person1.name}` : '',
      },
      p1RahuInP2D1H7: {
        present: p1RahuInP2D1H7,
        meaning: p1RahuInP2D1H7 ? `${person2.name} feels fascination, obsession, unusual pull toward ${person1.name}` : '',
      },
      p2RahuInP1D1H7: {
        present: p2RahuInP1D1H7,
        meaning: p2RahuInP1D1H7 ? `${person1.name} feels fascination, obsession, unusual pull toward ${person2.name}` : '',
      },
    },
    overallD9Rating: rating,
    interpretation,
  };
}

// Helper function for D9 analysis
function getSignLordInternal(signNum: number): PlanetName {
  const lords: Record<number, PlanetName> = {
    1: 'Mars', 2: 'Venus', 3: 'Mercury', 4: 'Moon',
    5: 'Sun', 6: 'Mercury', 7: 'Venus', 8: 'Mars',
    9: 'Jupiter', 10: 'Saturn', 11: 'Saturn', 12: 'Jupiter',
  };
  return lords[signNum] || 'Sun';
}

export function analyzeManglikDosha(
  person: PartnerChart,
): ManglikDoshaAnalysis {
  // Mars in houses 1, 2, 4, 7, 8, 12 from Lagna causes Manglik Dosha
  const manglikHouses = [1, 2, 4, 7, 8, 12];
  
  const marsHouse = person.mars.house;
  const isManglik = manglikHouses.includes(marsHouse);
  
  // House effects
  const houseEffects: Record<number, string> = {
    1: 'Dominance - tendency to control or overpower partner',
    2: 'Family disputes - conflicts with in-laws and family matters',
    4: 'Domestic unrest - home environment instability',
    7: 'Direct marital conflict - friction in partnership',
    8: 'Separation tendencies - challenges in sustaining relationship',
    12: 'Sexual frustration - intimacy and satisfaction issues',
  };
  
  const effect = isManglik ? houseEffects[marsHouse] : 'Mars favorably placed - no Manglik Dosha';
  
  // Check cancellation factors
  const cancellationFactors: string[] = [];
  
  console.log(`[Manglik Debug] ${person.name}`);
  console.log('  Mars sign:', person.mars.sign);
  console.log('  Mars state:', person.mars.state);
  console.log('  Mars aspectingPlanets:', person.mars.aspectingPlanets);
  
  // 1. Mars exalted (Capricorn = sign 10) or own sign (Aries = 1, Scorpio = 8)
  if (person.mars.state.exalted) {
    cancellationFactors.push('Mars exalted in Capricorn - strength overcomes affliction');
    console.log('  ✅ Cancellation: Mars exalted');
  } else if (person.mars.state.ownSign) {
    cancellationFactors.push('Mars in own sign (Aries/Scorpio) - comfortable placement neutralizes dosha');
    console.log('  ✅ Cancellation: Mars in own sign');
  }
  
  // 2. Aspect of Jupiter or Venus on Mars
  if (person.mars.aspectingPlanets.includes('Jupiter')) {
    cancellationFactors.push('Jupiter aspects Mars - wisdom neutralizes aggression');
    console.log('  ✅ Cancellation: Jupiter aspects Mars');
  }
  if (person.mars.aspectingPlanets.includes('Venus')) {
    cancellationFactors.push('Venus aspects Mars - love softens conflict');
    console.log('  ✅ Cancellation: Venus aspects Mars');
  }
  
  // 3. Mars in friendly sign with strength
  // Friendly signs for Mars: Aries(1), Leo(5), Scorpio(8), Sagittarius(9), Capricorn(10), Pisces(12)
  const marsFriendlySigns = [1, 5, 8, 9, 10, 12];
  console.log('  Checking friendly sign:', {
    marsSign: person.mars.sign,
    isFriendly: marsFriendlySigns.includes(person.mars.sign),
    debilitated: person.mars.state.debilitated,
    combust: person.mars.state.combust
  });
  
  if (marsFriendlySigns.includes(person.mars.sign) && !person.mars.state.debilitated && !person.mars.state.combust) {
    cancellationFactors.push('Mars in friendly sign with good strength - positive energy');
    console.log('  ✅ Cancellation: Mars in friendly sign with good strength');
  } else {
    console.log('  ❌ No friendly sign cancellation');
  }
  
  console.log(`  Total cancellations: ${cancellationFactors.length}`, cancellationFactors);
  
  // Note: "Both partners Manglik" cancellation will be checked at compatibility level
  
  const isCancelled = cancellationFactors.length > 0;
  
  // Determine severity
  let severity: 'high' | 'moderate' | 'low' | 'none' = 'none';
  if (isManglik && !isCancelled) {
    if ([7, 8].includes(marsHouse)) {
      severity = 'high'; // Direct marriage houses
    } else if ([1, 4].includes(marsHouse)) {
      severity = 'moderate';
    } else {
      severity = 'low'; // Houses 2, 12
    }
  }
  
  // Recommendation
  let recommendation = '';
  if (isManglik && isCancelled) {
    recommendation = 'Manglik Dosha is cancelled. Perform basic Mars remedies (Hanuman worship) for peace of mind.';
  } else if (isManglik && !isCancelled) {
    if (severity === 'high') {
      recommendation = 'Strong Manglik Dosha. Essential: Mars remedies (visit Hanuman temple Tuesdays, wear red coral after consultation), marry another Manglik, or perform Kumbh Vivah ritual.';
    } else if (severity === 'moderate') {
      recommendation = 'Moderate Manglik Dosha. Recommended: Mars remedies (chant Hanuman Chalisa, donate red items on Tuesdays).';
    } else {
      recommendation = 'Mild Manglik Dosha. Optional: Simple Mars remedies for harmony.';
    }
  } else {
    recommendation = 'No Manglik Dosha. Mars placement supports marital harmony.';
  }
  
  return {
    person: person.name,
    isManglik,
    marsHouse,
    effect,
    severity,
    cancellationFactors,
    isCancelled,
    recommendation,
  };
}

export function calculateAshtakuta(person1: PartnerChart, person2: PartnerChart): AshtakutaScore {
  let totalScore = 0;
  
  // Normalize nakshatra names
  const nak1 = normalizeNakshatraName(person1.moonNakshatra);
  const nak2 = normalizeNakshatraName(person2.moonNakshatra);
  
  // 1. Varna (Caste) - 1 point
  const varna1 = NAKSHATRA_TO_VARNA[nak1] || 'Vaishya';
  const varna2 = NAKSHATRA_TO_VARNA[nak2] || 'Vaishya';
  const varnaOrder = { 'Brahmin': 4, 'Kshatriya': 3, 'Vaishya': 2, 'Shudra': 1 };
  const varnaScore = varnaOrder[varna1] >= varnaOrder[varna2] ? 1 : 0;
  totalScore += varnaScore;
  
  // 2. Vashya (Mutual Attraction) - 2 points
  const vashya1 = NAKSHATRA_TO_VASHYA[nak1] || 'Manava';
  const vashya2 = NAKSHATRA_TO_VASHYA[nak2] || 'Manava';
  let vashyaScore = 0;
  if (vashya1 === vashya2) vashyaScore = 2;
  else if (
    (vashya1 === 'Manava' && vashya2 === 'Chatushpada') ||
    (vashya1 === 'Chatushpada' && vashya2 === 'Manava') ||
    (vashya1 === 'Jalachara' && vashya2 === 'Vanachara') ||
    (vashya1 === 'Vanachara' && vashya2 === 'Jalachara')
  ) vashyaScore = 1;
  totalScore += vashyaScore;
  
  // 3. Tara (Birth Star) - 3 points
  const nakshatraNames = Object.keys(NAKSHATRA_DATA);
  const nakIndex1 = nakshatraNames.indexOf(nak1);
  const nakIndex2 = nakshatraNames.indexOf(nak2);
  const taraDiff = Math.abs(nakIndex1 - nakIndex2) % 9;
  const taraScore = [3, 0, 3, 3, 0, 0, 3, 0, 0][taraDiff] || 0;
  totalScore += taraScore;
  
  // 4. Yoni (Sexual Compatibility) - 4 points
  const yoni1 = NAKSHATRA_DATA[nak1]?.yoni || 'Unknown';
  const yoni2 = NAKSHATRA_DATA[nak2]?.yoni || 'Unknown';
  let yoniScore = 0;
  if (yoni1 === yoni2) yoniScore = 4;
  else if (YONI_FRIENDS[yoni1]?.includes(yoni2)) yoniScore = 3;
  else if (!YONI_ENEMIES[yoni1]?.includes(yoni2)) yoniScore = 2;
  else yoniScore = 0;
  totalScore += yoniScore;
  
  // 5. Graha Maitri (Planetary Friendship) - 5 points
  const lord1 = person1.moonNakshatraLord;
  const lord2 = person2.moonNakshatraLord;
  const relationship = getPlanetaryRelationship(lord1, lord2);
  const grahaMaitriScore = 
    relationship === 'same' ? 5 :
    relationship === 'friendly' ? 4 :
    relationship === 'neutral' ? 3 : 1;
  totalScore += grahaMaitriScore;
  
  // 6. Gana (Temperament) - 6 points
  const gana1 = NAKSHATRA_DATA[nak1]?.gana || 'Manushya';
  const gana2 = NAKSHATRA_DATA[nak2]?.gana || 'Manushya';
  let ganaScore = 0;
  if (gana1 === gana2) ganaScore = 6;
  else if (
    (gana1 === 'Deva' && gana2 === 'Manushya') ||
    (gana1 === 'Manushya' && gana2 === 'Deva') ||
    (gana1 === 'Manushya' && gana2 === 'Rakshasa') ||
    (gana1 === 'Rakshasa' && gana2 === 'Manushya')
  ) ganaScore = 3;
  else ganaScore = 0; // Deva-Rakshasa
  totalScore += ganaScore;
  
  // 7. Rashi (Moon Sign Compatibility) - 7 points
  // Traditional scoring: same=7, 2/12=5, 3/11=3, 4/10=5, 5/9=3, 6/8=3, 7=4
  const signDistance = Math.abs(person1.moonSign - person2.moonSign);
  let rashiScore = 0;
  if (signDistance === 0) rashiScore = 7;        // Same sign
  else if (signDistance === 7) rashiScore = 4;   // 7th house (opposite)
  else if (signDistance === 1 || signDistance === 11) rashiScore = 5;  // 2nd/12th
  else if (signDistance === 4 || signDistance === 10) rashiScore = 5;  // 4th/10th
  else if (signDistance === 3 || signDistance === 9) rashiScore = 3;   // 3rd/11th
  else if (signDistance === 5 || signDistance === 6 || signDistance === 8) rashiScore = 3; // 5th/9th, 6th/8th
  else rashiScore = 2;  // 2nd/12th alternate calculation
  totalScore += rashiScore;
  
  // 8. Nadi (Health/Progeny) - 8 points
  const nak1Normalized = normalizeNakshatraName(nak1);
  const nak2Normalized = normalizeNakshatraName(nak2);
  const nadi1 = NAKSHATRA_TO_NADI[nak1Normalized] || 'Vata';
  const nadi2 = NAKSHATRA_TO_NADI[nak2Normalized] || 'Vata';
  const nadiScore = nadi1 !== nadi2 ? 8 : 0;
  
  console.log(`[Nadi Check] ${nak1} (${nak1Normalized}) = ${nadi1}, ${nak2} (${nak2Normalized}) = ${nadi2}, Score: ${nadiScore}`);
  
  totalScore += nadiScore;
  
  const rating: 'excellent' | 'good' | 'average' | 'poor' =
    totalScore >= 28 ? 'excellent' :
    totalScore >= 20 ? 'good' :
    totalScore >= 14 ? 'average' : 'poor';
  
  return {
    varna: { score: varnaScore, max: 1, description: `${varna1} → ${varna2}` },
    vashya: { score: vashyaScore, max: 2, description: `${vashya1} ↔ ${vashya2}` },
    tara: { score: taraScore, max: 3, description: `Birth star ${taraDiff} apart` },
    yoni: { score: yoniScore, max: 4, description: `${yoni1} ↔ ${yoni2}` },
    grahaMaitri: { score: grahaMaitriScore, max: 5, description: `${lord1} ↔ ${lord2}` },
    gana: { score: ganaScore, max: 6, description: `${gana1}–${gana2}` },
    rashi: { score: rashiScore, max: 7, description: `${signDistance} signs apart` },
    nadi: { score: nadiScore, max: 8, description: `${nadi1} vs ${nadi2}` },
    total: totalScore,
    rating,
  };
}

// ===== MAIN COMPATIBILITY FUNCTION =====

export function calculateMarriageCompatibility(
  person1: PartnerChart,
  person2: PartnerChart,
  precomputedManglik1?: ManglikDoshaAnalysis,  // Optional: use existing Mangal Dosha data
  precomputedManglik2?: ManglikDoshaAnalysis   // Optional: use existing Mangal Dosha data
): MarriageCompatibilityResult {
  const moonDistance = analyzeMoonDistance(person1, person2);
  const gana = analyzeGanaCompatibility(person1, person2);
  const nakshatraLord = analyzeNakshatraLordCompatibility(person1, person2);
  const yoni = analyzeYoniCompatibility(person1, person2);
  const lagna = analyzeLagnaCompatibility(person1, person2);
  const seventhHouseP1 = analyzeSeventhHouse(person1);
  const seventhHouseP2 = analyzeSeventhHouse(person2);
  const venusJupiterP1 = analyzeVenusJupiter(person1);
  const venusJupiterP2 = analyzeVenusJupiter(person2);
  const ashtakuta = calculateAshtakuta(person1, person2);
  
  // Analyze Navamsa (D9) compatibility - marriage reality
  const d9Analysis = analyzeD9Compatibility(person1, person2);
  
  // Analyze Manglik Dosha for both partners (use precomputed if available)
  let manglikP1 = precomputedManglik1 || analyzeManglikDosha(person1);
  let manglikP2 = precomputedManglik2 || analyzeManglikDosha(person2);
  
  // Check for "Both partners Manglik" cancellation
  if (manglikP1.isManglik && manglikP2.isManglik) {
    manglikP1 = {
      ...manglikP1,
      cancellationFactors: [...manglikP1.cancellationFactors, 'Both partners are Manglik - dosha neutralized'],
      isCancelled: true,
      recommendation: 'Both partners are Manglik - dosha is cancelled. Perfect match for Manglik consideration. Basic Mars remedies recommended for harmony.',
    };
    manglikP2 = {
      ...manglikP2,
      cancellationFactors: [...manglikP2.cancellationFactors, 'Both partners are Manglik - dosha neutralized'],
      isCancelled: true,
      recommendation: 'Both partners are Manglik - dosha is cancelled. Perfect match for Manglik consideration. Basic Mars remedies recommended for harmony.',
    };
  }
  
  // ===== CALCULATE OVERALL SCORE (0-100) =====
  // New scoring system with refined weights and multiplicative D9 balance
  
  let totalScore = 0;
  
  // 1. Ashtakuta: 15 points (reduced from 25)
  const ashtakutaPercent = (ashtakuta.total / 36) * 100;
  const ashtakutaPoints = (ashtakutaPercent / 100) * 15;
  totalScore += ashtakutaPoints;
  
  // 2. Moon Distance: 14 points (increased from 10)
  const moonDistancePercent = moonDistance.compatibilityPercent;
  
  // Cap Moon Distance if 6-8 signs apart (conflicting wavelength)
  const signDistance = Math.abs(person1.moonSign - person2.moonSign);
  let moonDistanceScore = (moonDistancePercent / 100) * 14;
  if (signDistance >= 6 && signDistance <= 8 && d9Analysis) {
    // Cap at 40-50% unless D9 compensates
    const d9RatingMultiplier = d9Analysis.overallD9Rating === 'excellent' ? 1.0 :
                                d9Analysis.overallD9Rating === 'good' ? 0.8 : 0.5;
    const maxMoonScore = 14 * 0.45 * d9RatingMultiplier; // 45% base cap, adjusted by D9
    moonDistanceScore = Math.min(moonDistanceScore, maxMoonScore);
  }
  totalScore += moonDistanceScore;
  
  // 3. D9 Navamsa (Marriage Reality): 31 points (25 base + 6 balance bonus)
  let strongStrongCount = 0;
  let weakWeakCount = 0;
  
  if (d9Analysis) {
    // Base D9 rating
    let d9BaseScore = d9Analysis.overallD9Rating === 'excellent' ? 25 :
                      d9Analysis.overallD9Rating === 'good' ? 19 :
                      d9Analysis.overallD9Rating === 'fair' ? 13 : 6;
    
    // Cross-chart refinements
    if (d9Analysis.crossChartChecks.p1VenusInP2D9H7 || d9Analysis.crossChartChecks.p2VenusInP1D9H7) {
      d9BaseScore += 1.5; // Reduced from +2
    }
    if (d9Analysis.crossChartChecks.p1LagnaLordInP2D9Lagna || d9Analysis.crossChartChecks.p2LagnaLordInP1D9Lagna) {
      d9BaseScore += 0.5; // Reduced from +1
    }
    if (d9Analysis.crossChartChecks.p1MarsAfflictsP2D9H7 || d9Analysis.crossChartChecks.p2MarsAfflictsP1D9H7) {
      d9BaseScore -= 2;
    }
    if (d9Analysis.crossChartChecks.p1MarsAspectsP2D9H7 || d9Analysis.crossChartChecks.p2MarsAspectsP1D9H7) {
      d9BaseScore -= 1;
    }
    
    // Venus/Jupiter weakness penalty (inside D9)
    const p1VenusWeak = d9Analysis.p1D9Condition.venusStrength === 'weak';
    const p2VenusWeak = d9Analysis.p2D9Condition.venusStrength === 'weak';
    const p1JupiterWeak = venusJupiterP1.jupiter.strength === 'weak';
    const p2JupiterWeak = venusJupiterP2.jupiter.strength === 'weak';
    
    if ((p1VenusWeak && p2VenusWeak) || (p1JupiterWeak && p2JupiterWeak)) {
      d9BaseScore -= 2; // Both partners have weak love/wisdom capacity
    }
    
    // Balance Pattern: MULTIPLICATIVE (not additive)
    const p1Balance = d9Analysis.p1D9Condition.balancePattern;
    const p2Balance = d9Analysis.p2D9Condition.balancePattern;
    
    let balanceMultiplier = 1.0;
    let balanceBonus = 0;
    
    // Average balance patterns
    strongStrongCount = (p1Balance === 'strong-strong' ? 1 : 0) + (p2Balance === 'strong-strong' ? 1 : 0);
    weakWeakCount = (p1Balance === 'weak-weak' ? 1 : 0) + (p2Balance === 'weak-weak' ? 1 : 0);
    
    if (strongStrongCount === 2) {
      balanceMultiplier = 1.1; // Both have resilient balance
    } else if (strongStrongCount === 1 && weakWeakCount === 0) {
      balanceMultiplier = 1.05; // One resilient, one mixed
    } else if (weakWeakCount >= 1) {
      balanceMultiplier = 0.85; // One or both have burnout risk
    }
    
    d9BaseScore = d9BaseScore * balanceMultiplier;
    
    // Add separate balance bonus (up to 6 points)
    if (strongStrongCount === 2) balanceBonus = 6;
    else if (strongStrongCount === 1) balanceBonus = 3;
    else if (weakWeakCount === 0) balanceBonus = 1;
    
    const d9TotalScore = Math.min(31, d9BaseScore + balanceBonus); // Cap at 31
    totalScore += d9TotalScore;
  }
  
  // 4. Venus/Jupiter Strength: 15 points (reduced from 20)
  const venusJupiterAvg = (
    (venusJupiterP1.venus.strength === 'strong' ? 85 : venusJupiterP1.venus.strength === 'moderate' ? 65 : 40) +
    (venusJupiterP1.jupiter.strength === 'strong' ? 85 : venusJupiterP1.jupiter.strength === 'moderate' ? 65 : 40) +
    (venusJupiterP2.venus.strength === 'strong' ? 85 : venusJupiterP2.venus.strength === 'moderate' ? 65 : 40) +
    (venusJupiterP2.jupiter.strength === 'strong' ? 85 : venusJupiterP2.jupiter.strength === 'moderate' ? 65 : 40)
  ) / 4;
  const venusJupiterPoints = (venusJupiterAvg / 100) * 15;
  totalScore += venusJupiterPoints;
  
  // 5. 7th House Analysis: 10 points
  const h7P1Score = (person1.h7Score / 100) * 5;
  const h7P2Score = (person2.h7Score / 100) * 5;
  totalScore += h7P1Score + h7P2Score;
  
  // Jupiter aspect on 7th house bonus: +2 points per partner
  if (seventhHouseP1.jupiterProtection) totalScore += 2;
  if (seventhHouseP2.jupiterProtection) totalScore += 2;
  
  // 6. Lagna Compatibility: 5 points
  const lagnaPoints = (lagna.compatibilityPercent / 100) * 5;
  totalScore += lagnaPoints;
  
  // 7. Gana/Yoni/Nakshatra Lord: 5 points combined
  const ganaPoints = (gana.compatibilityPercent / 100) * 2;
  const yoniPoints = (yoni.compatibilityPercent / 100) * 2;
  const nakshatraPoints = (nakshatraLord.compatibilityPercent / 100) * 1;
  totalScore += ganaPoints + yoniPoints + nakshatraPoints;
  
  // 8. D1 Overlays: +5 to -5 points
  let d1OverlayPoints = 0;
  if (d9Analysis?.d1Overlays) {
    // Venus in 7th: +2 each
    if (d9Analysis.d1Overlays.p1VenusInP2D1H7.present) d1OverlayPoints += 2;
    if (d9Analysis.d1Overlays.p2VenusInP1D1H7.present) d1OverlayPoints += 2;
    
    // Moon in 4th: +1.5 each
    if (d9Analysis.d1Overlays.p1MoonInP2D1H4.present) d1OverlayPoints += 1.5;
    if (d9Analysis.d1Overlays.p2MoonInP1D1H4.present) d1OverlayPoints += 1.5;
    
    // Saturn in 7th: +1 each (karmic stability)
    if (d9Analysis.d1Overlays.p1SaturnInP2D1H7.present) d1OverlayPoints += 1;
    if (d9Analysis.d1Overlays.p2SaturnInP1D1H7.present) d1OverlayPoints += 1;
    
    // Rahu in 7th: -1 each (obsession risk)
    if (d9Analysis.d1Overlays.p1RahuInP2D1H7.present) d1OverlayPoints -= 1;
    if (d9Analysis.d1Overlays.p2RahuInP1D1H7.present) d1OverlayPoints -= 1;
    
    // Mars in 7th: 0 (passion + friction cancel out)
  }
  d1OverlayPoints = Math.max(-5, Math.min(5, d1OverlayPoints)); // Cap at ±5
  totalScore += d1OverlayPoints;
  
  // 9. Manglik Dosha Penalty (dynamic)
  if (manglikP1.isManglik && !manglikP1.isCancelled) {
    let penalty = manglikP1.severity === 'high' ? -10 :
                  manglikP1.severity === 'moderate' ? -7 : -5;
    
    // Halve penalty if Mars is well dignified (exalted/own sign)
    if (person1.mars.state.exalted || person1.mars.state.ownSign) {
      penalty = penalty / 2;
    }
    totalScore += penalty;
  }
  
  if (manglikP2.isManglik && !manglikP2.isCancelled) {
    let penalty = manglikP2.severity === 'high' ? -10 :
                  manglikP2.severity === 'moderate' ? -7 : -5;
    
    // Halve penalty if Mars is well dignified
    if (person2.mars.state.exalted || person2.mars.state.ownSign) {
      penalty = penalty / 2;
    }
    totalScore += penalty;
  }
  
  // 10. Nadi Dosha Penalty (dynamic)
  if (ashtakuta.nadi.score === 0) {
    // Base penalty: -8
    let nadiPenalty = -8;
    
    // Reduce to -4 if D9 is strong AND Jupiter is strong (both partners)
    if (d9Analysis) {
      const d9Strong = d9Analysis.overallD9Rating === 'excellent' || d9Analysis.overallD9Rating === 'good';
      const jupiterStrong = (venusJupiterP1.jupiter.strength === 'strong' && venusJupiterP2.jupiter.strength === 'strong');
      
      if (d9Strong && jupiterStrong) {
        nadiPenalty = -4;
      }
    }
    
    totalScore += nadiPenalty;
  }
  
  // Final score (capped at 0-100)
  const overallScore = Math.max(0, Math.min(100, Math.round(totalScore)));
  
  const overallRating: 'excellent' | 'good' | 'fair' | 'challenging' =
    overallScore >= 85 ? 'excellent' :   // Soulmate potential
    overallScore >= 70 ? 'good' :        // Highly recommended
    overallScore >= 55 ? 'fair' :        // Manageable challenges
    'challenging';                        // High risk without remedies
  
  // Generate strengths
  const strengths: string[] = [];
  if (ashtakuta.total >= 28) strengths.push(`Excellent Ashtakuta score (${ashtakuta.total}/36)`);
  if (d9Analysis && d9Analysis.overallD9Rating === 'excellent') strengths.push(`Exceptional D9 marriage reality (${d9Analysis.interpretation})`);
  if (strongStrongCount >= 1) strengths.push(`Resilient H1-H7 balance - both partners maintain identity in marriage`);
  if (seventhHouseP1.jupiterProtection) strengths.push(`${person1.name}: Jupiter's protective aspect on 7th house - divine blessing for marriage`);
  if (seventhHouseP2.jupiterProtection) strengths.push(`${person2.name}: Jupiter's protective aspect on 7th house - divine blessing for marriage`);
  if (d9Analysis?.d1Overlays?.p1VenusInP2D1H7.present || d9Analysis?.d1Overlays?.p2VenusInP1D1H7.present) {
    strengths.push('Natural spouse-like attraction in daily life (Venus overlay)');
  }
  if (d9Analysis?.d1Overlays?.p1MoonInP2D1H4.present || d9Analysis?.d1Overlays?.p2MoonInP1D1H4.present) {
    strengths.push('Deep emotional home feeling with partner (Moon in 4th overlay)');
  }
  if (moonDistance.score === 'high') strengths.push(`Strong emotional harmony (${moonDistance.relationshipName})`);
  if (gana.adjustment === 'easy') strengths.push(`Compatible temperaments (${gana.combo})`);
  if (nakshatraLord.relationship === 'same' || nakshatraLord.relationship === 'friendly') 
    strengths.push('Good psychological compatibility');
  if (yoni.relationship === 'same' || yoni.relationship === 'friendly')
    strengths.push('Natural physical attraction');
  if (lagna.compatibilityPercent >= 75) strengths.push(`Aligned life paths (${lagna.distance})`);
  if (venusJupiterP1.venus.strength === 'strong' && venusJupiterP2.venus.strength === 'strong')
    strengths.push('Both have strong capacity to love');
  
  // Generate challenges
  const challenges: string[] = [];
  if (ashtakuta.total < 18) challenges.push(`Low Ashtakuta score (${ashtakuta.total}/36) - major compatibility issues`);
  if (d9Analysis && d9Analysis.overallD9Rating === 'challenging') challenges.push(`⚠️ D9 reveals marriage challenges - ${d9Analysis.interpretation}`);
  if (weakWeakCount >= 1) challenges.push(`⚠️ Weak H1-H7 balance - risk of identity loss and marriage burnout`);
  if (d9Analysis?.d1Overlays?.p1RahuInP2D1H7.present || d9Analysis?.d1Overlays?.p2RahuInP1D1H7.present) {
    challenges.push('Obsessive/unusual attraction patterns (Rahu in 7th overlay) - requires conscious boundaries');
  }
  if (d9Analysis?.d1Overlays?.p1MarsInP2D1H7.present || d9Analysis?.d1Overlays?.p2MarsInP1D1H7.present) {
    challenges.push('Daily passion mixed with arguments (Mars in 7th overlay) - manage conflict mindfully');
  }
  if (signDistance >= 6 && signDistance <= 8) challenges.push(`⚠️ Moon 6-8 signs apart - conflicting emotional wavelengths require extra effort`);
  if (ashtakuta.nadi.score === 0) {
    const nadiSeverity = (d9Analysis && (d9Analysis.overallD9Rating === 'excellent' || d9Analysis.overallD9Rating === 'good')) && 
                         (venusJupiterP1.jupiter.strength === 'strong' && venusJupiterP2.jupiter.strength === 'strong')
                         ? 'MODERATE' : 'CRITICAL';
    challenges.push(`⚠️ ${nadiSeverity}: Nadi dosha - health/progeny concerns ${nadiSeverity === 'MODERATE' ? '(mitigated by strong D9/Jupiter)' : '(requires remedies)'}`);
  }
  if (manglikP1.isManglik && !manglikP1.isCancelled) {
    const severityText = (person1.mars.state.exalted || person1.mars.state.ownSign) ? '(reduced - Mars dignified)' : '';
    challenges.push(`⚠️ ${person1.name} has uncancelled Manglik Dosha (${manglikP1.severity} severity) ${severityText} - ${manglikP1.effect.split(' - ')[1]}`);
  }
  if (manglikP2.isManglik && !manglikP2.isCancelled) {
    const severityText = (person2.mars.state.exalted || person2.mars.state.ownSign) ? '(reduced - Mars dignified)' : '';
    challenges.push(`⚠️ ${person2.name} has uncancelled Manglik Dosha (${manglikP2.severity} severity) ${severityText} - ${manglikP2.effect.split(' - ')[1]}`);
  }
  if (moonDistance.score === 'low') challenges.push(`Emotional incompatibility (${moonDistance.relationshipName})`);
  if (gana.adjustment === 'major-adjustment') challenges.push(`Temperament clash (${gana.combo})`);
  if (yoni.relationship === 'enemy') challenges.push('Physical/sexual mismatch - requires openness');
  if (lagna.flags.includes('Major ego conflicts')) challenges.push('Ego friction in daily life');
  if (venusJupiterP1.venus.strength === 'weak' || venusJupiterP2.venus.strength === 'weak')
    challenges.push('One or both struggle with love expression');
  if (venusJupiterP1.jupiter.strength === 'weak' || venusJupiterP2.jupiter.strength === 'weak')
    challenges.push('Wisdom deficiency - poor relationship decisions');
  
  // Generate recommendations
  const recommendations: string[] = [];
  if (manglikP1.isManglik || manglikP2.isManglik) {
    if (manglikP1.isManglik && manglikP2.isManglik && manglikP1.isCancelled) {
      recommendations.push('Both Manglik - dosha cancelled. Continue with Hanuman worship for marital bliss.');
    } else if ((manglikP1.isManglik && !manglikP1.isCancelled) || (manglikP2.isManglik && !manglikP2.isCancelled)) {
      recommendations.push('Uncancelled Manglik Dosha present. CRITICAL: Perform Mars remedies before marriage (Hanuman temple visits, red coral gemstone consultation, or Kumbh Vivah ritual).');
    }
  }
  if (moonDistance.score === 'low' || moonDistance.score === 'conditional') {
    recommendations.push('Regular emotional check-ins and counseling recommended');
  }
  if (gana.adjustment === 'major-adjustment' || gana.adjustment === 'requires-work') {
    recommendations.push('Pre-marital counseling essential for temperament differences');
  }
  if (yoni.relationship === 'enemy' || yoni.relationship === 'neutral') {
    recommendations.push('Open communication about physical needs and expectations');
  }
  if (venusJupiterP1.venus.strength === 'weak' || venusJupiterP2.venus.strength === 'weak') {
    recommendations.push('Venus remedies (wear diamond/white sapphire) and love-language work');
  }
  if (venusJupiterP1.jupiter.strength === 'weak' || venusJupiterP2.jupiter.strength === 'weak') {
    recommendations.push('Jupiter remedies (yellow sapphire) and seek relationship mentoring');
  }
  if (lagna.flags.length > 0) {
    recommendations.push('Conscious ego management and individual space allocation');
  }
  
  return {
    moonDistance,
    gana,
    nakshatraLord,
    yoni,
    lagna,
    seventhHouseP1,
    seventhHouseP2,
    venusJupiterP1,
    venusJupiterP2,
    ashtakuta,
    manglikP1,
    manglikP2,
    d9Analysis,
    overallScore,
    overallRating,
    strengths: strengths.length > 0 ? strengths : ['Compatibility requires conscious effort'],
    challenges: challenges.length > 0 ? challenges : ['No major red flags identified'],
    recommendations: recommendations.length > 0 ? recommendations : ['Continue building trust and communication'],
  };
}
